import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useVTTSocket } from "@/hooks/useVTTSocket";
import { VTTToolbar } from "@/components/VTTToolbar";
import { SPELL_SCHOOLS, getSpellSVG } from "@/data/spellEffects";
import { findAsset } from "@/data/mapAssets";
import { Heart, Shield, Package, ZoomIn, ZoomOut, RotateCcw, Dices, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/files`;

const PlayerVTTPage = () => {
  const { campaignId } = useParams();
  const [session, setSession] = useState(null);
  const [character, setCharacter] = useState(null);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedToken, setSelectedToken] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [activeTool, setActiveTool] = useState("select");
  const [measurePersistent, setMeasurePersistent] = useState(false);
  const [viewport, setViewport] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, vx: 0, vy: 0 });
  const canvasAreaRef = useRef(null);
  const [shapes, setShapes] = useState([]);
  const [pings, setPings] = useState([]);
  const [drawStart, setDrawStart] = useState(null);
  const [drawPreview, setDrawPreview] = useState(null);
  const [diceRolls, setDiceRolls] = useState([]);
  const [tokenInfoOpen, setTokenInfoOpen] = useState(null);
  const pinchRef = useRef({ active: false, startDist: 0, startScale: 1, startX: 0, startY: 0, startVx: 0, startVy: 0 });
  const [tokenAnim, setTokenAnim] = useState({});
  const tokenAnimTimersRef = useRef({});
  const prevTokensRef = useRef({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/guest/portal/${campaignId}/vtt-session`, { withCredentials: true });
        setSession(res.data.session);
        setCharacter(res.data.character);
        setMember(res.data.member);
        if (res.data.character?.id) {
          const eqRes = await axios.get(`${API}/characters/${res.data.character.id}/equipment`, { withCredentials: true }).catch(() => ({ data: [] }));
          setEquipment(eqRes.data);
        }
      } catch { toast.error("Failed to load VTT session"); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [campaignId]);

  // Detect token HP / death changes to fire animations
  useEffect(() => {
    const tokens = session?.tokens || [];
    const prev = prevTokensRef.current;
    const trigger = (id, type, dur) => {
      setTokenAnim(p => ({ ...p, [id]: type }));
      if (tokenAnimTimersRef.current[id]) clearTimeout(tokenAnimTimersRef.current[id]);
      tokenAnimTimersRef.current[id] = setTimeout(() => {
        setTokenAnim(p => { const { [id]: _, ...rest } = p; return rest; });
        delete tokenAnimTimersRef.current[id];
      }, dur);
    };
    tokens.forEach(t => {
      const p = prev[t.id];
      if (p) {
        if (!p.is_dead && t.is_dead) trigger(t.id, "death", 900);
        else if (typeof t.hp === "number" && typeof p.hp === "number") {
          if (t.hp < p.hp) trigger(t.id, "damage", 600);
          else if (t.hp > p.hp) trigger(t.id, "heal", 700);
        }
      }
      prev[t.id] = { hp: t.hp, is_dead: t.is_dead };
    });
    Object.keys(prev).forEach(id => { if (!tokens.find(t => t.id === id)) delete prev[id]; });
  }, [session?.tokens]);

  const [sharedHandout, setSharedHandout] = useState(null);

  const { emitPing, emitMeasure, emitDiceRoll } = useVTTSocket({
    sessionId: session?.id,
    playerName: member?.character_name || "Player",
    onTokenMoved: (d) => setSession(p => p ? { ...p, tokens: p.tokens.map(t => t.id === d.token_id ? { ...t, x: d.x, y: d.y } : t) } : p),
    onHpUpdated: (d) => setSession(p => p ? { ...p, tokens: p.tokens.map(t => t.id === d.token_id ? { ...t, hp: d.hp } : t) } : p),
    onTokenAdded: (tok) => setSession(p => p ? { ...p, tokens: [...(p.tokens || []), tok] } : p),
    onTokenRemoved: (d) => setSession(p => p ? { ...p, tokens: p.tokens.filter(t => t.id !== d.token_id) } : p),
    onFogChanged: (d) => setSession(p => p ? { ...p, fog_areas: d.fog_areas } : p),
    onSpellsChanged: (d) => setSession(p => p ? { ...p, spells: d.spells || [] } : p),
    onAssetsChanged: (d) => setSession(p => p ? { ...p, assets: d.assets || [] } : p),
    onPingReceived: (d) => { const pid = Date.now() + Math.random(); setPings(p => [...p, { id: pid, x: d.x, y: d.y, name: d.player_name }]); setTimeout(() => setPings(p => p.filter(x => x.id !== pid)), 4500); },
    onShapeDrawn: (d) => { if (d.type) { const s = { ...d, id: Date.now() }; setShapes(p => [...p, s]); setTimeout(() => setShapes(p => p.filter(sh => sh.id !== s.id)), 3000); } },
    onDiceRolled: (d) => {
      const roll = { id: Date.now(), roller: d.roller, formula: d.formula, rolls: d.rolls, modifier: d.modifier, total: d.total, timestamp: new Date().toLocaleTimeString() };
      setDiceRolls(p => [roll, ...p].slice(0, 20));
      toast.info(`${d.roller} rolled ${d.formula}: ${d.total}`);
    },
    onHandoutShared: (d) => {
      setSharedHandout({ name: d.name, content: d.content, image_url: d.image_url });
      toast.info(`DM shared: ${d.name}`);
    },
    onPlayerJoined: (d) => toast.info(`${d.player_name} joined`),
    onPlayerLeft: () => {},
  });

  const gs = session?.grid_size || 50;

  const getMapUrl = () => {
    if (!session?.map_url || session.map_url === "None") return null;
    const u = session.map_url;
    return u.startsWith("http") ? u : `${FILE_URL}/${u}`;
  };
  const isInternalMap = () => session?.map_url && !session.map_url.startsWith("http");

  const handleWheel = (e) => {
    e.preventDefault();
    setViewport(v => {
      const f = e.deltaY < 0 ? 1.1 : 0.9;
      const ns = Math.max(0.2, Math.min(4, v.scale * f));
      const rect = canvasAreaRef.current?.getBoundingClientRect();
      if (!rect) return { ...v, scale: ns };
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      return { x: mx - (mx - v.x) * (ns / v.scale), y: my - (my - v.y) * (ns / v.scale), scale: ns };
    });
  };

  // Pinch-to-zoom for touch devices
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const t1 = e.touches[0], t2 = e.touches[1];
      const rect = canvasAreaRef.current?.getBoundingClientRect();
      pinchRef.current = {
        active: true,
        startDist: Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY),
        startScale: viewport.scale,
        startX: (t1.clientX + t2.clientX) / 2 - (rect?.left || 0),
        startY: (t1.clientY + t2.clientY) / 2 - (rect?.top || 0),
        startVx: viewport.x,
        startVy: viewport.y,
      };
      setIsPanning(false);
    }
  };
  const handleTouchMove = (e) => {
    if (pinchRef.current.active && e.touches.length === 2) {
      e.preventDefault();
      const t1 = e.touches[0], t2 = e.touches[1];
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const { startDist, startScale, startX, startY, startVx, startVy } = pinchRef.current;
      const ns = Math.max(0.2, Math.min(4, startScale * (dist / startDist)));
      const ratio = ns / startScale;
      setViewport({ x: startX - (startX - startVx) * ratio, y: startY - (startY - startVy) * ratio, scale: ns });
    }
  };
  const handleTouchEnd = (e) => { if (e.touches.length < 2) pinchRef.current.active = false; };

  const getWorldPos = (e) => {
    const rect = canvasAreaRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: (e.clientX - rect.left - viewport.x) / viewport.scale, y: (e.clientY - rect.top - viewport.y) / viewport.scale };
  };

  const handlePointerDown = (e) => {
    if (e.button === 2 || e.button === 1 || (e.button === 0 && e.shiftKey)) {
      setIsPanning(true);
      panStartRef.current = { x: e.clientX, y: e.clientY, vx: viewport.x, vy: viewport.y };
      return;
    }
    if (e.button !== 0) return;
    const pos = getWorldPos(e);
    // Alt+Click anywhere → quick ping
    if (e.altKey) { const pid = Date.now() + Math.random(); setPings(p => [...p, { id: pid, x: pos.x, y: pos.y, name: "You" }]); emitPing(pos.x, pos.y); setTimeout(() => setPings(p => p.filter(x => x.id !== pid)), 4500); return; }
    if (activeTool === "ping") { const pid = Date.now() + Math.random(); setPings(p => [...p, { id: pid, x: pos.x, y: pos.y, name: "You" }]); emitPing(pos.x, pos.y); setTimeout(() => setPings(p => p.filter(x => x.id !== pid)), 4500); return; }
    if (activeTool?.startsWith("measure_")) { setDrawStart(pos); return; }
  };
  const handlePointerMove = (e) => {
    if (isPanning) { setViewport(v => ({ ...v, x: panStartRef.current.vx + (e.clientX - panStartRef.current.x), y: panStartRef.current.vy + (e.clientY - panStartRef.current.y) })); return; }
    if (drawStart && activeTool?.startsWith("measure_")) { setDrawPreview({ startX: drawStart.x, startY: drawStart.y, endX: getWorldPos(e).x, endY: getWorldPos(e).y, type: activeTool }); }
  };
  const handlePointerUp = (e) => {
    setIsPanning(false);
    if (drawStart && activeTool?.startsWith("measure_")) {
      const pos = getWorldPos(e);
      if (Math.abs(pos.x - drawStart.x) > 5 || Math.abs(pos.y - drawStart.y) > 5) {
        const shape = { id: Date.now(), startX: drawStart.x, startY: drawStart.y, endX: pos.x, endY: pos.y, type: activeTool };
        setShapes(s => [...s, shape]);
        emitMeasure(shape);
        if (!measurePersistent) setTimeout(() => setShapes(s => s.filter(sh => sh.id !== shape.id)), 3000);
      }
    }
    setDrawStart(null);
    setDrawPreview(null);
  };

  const rollDice = (formula) => {
    const m = formula.match(/(\d+)d(\d+)([+-]\d+)?/);
    if (!m) return;
    const count = parseInt(m[1]), sides = parseInt(m[2]), mod = m[3] ? parseInt(m[3]) : 0;
    let total = mod;
    const rolls = [];
    for (let i = 0; i < count; i++) { const r = Math.floor(Math.random() * sides) + 1; rolls.push(r); total += r; }
    const roller = member?.character_name || "Player";
    const roll = { id: Date.now(), roller, formula, rolls, modifier: mod, total, timestamp: new Date().toLocaleTimeString() };
    setDiceRolls(p => [roll, ...p].slice(0, 20));
    emitDiceRoll({ roller, formula, rolls, modifier: mod, total });
    toast.success(`Rolled ${formula}: ${total}`);
  };

  const MeasureShape = ({ shape, preview }) => {
    const op = preview ? 0.4 : 0.7;
    const { startX: sx, startY: sy, endX: ex, endY: ey, type } = shape;
    if (type === "measure_line") { const d = Math.sqrt((ex-sx)**2+(ey-sy)**2); return <g><line x1={sx} y1={sy} x2={ex} y2={ey} stroke="#00ff88" strokeWidth="3" opacity={op}/>{!preview&&<text x={(sx+ex)/2+8} y={(sy+ey)/2-8} fill="#00ff88" fontSize="14" fontWeight="bold">{Math.round(d/gs*5)}ft</text>}</g>; }
    if (type === "measure_circle") { const r = Math.sqrt((ex-sx)**2+(ey-sy)**2); return <circle cx={sx} cy={sy} r={r} fill="rgba(100,100,255,0.12)" stroke="#6666ff" strokeWidth="2" opacity={op}/>; }
    if (type === "measure_cone") { const dx=ex-sx,dy=ey-sy,a=Math.atan2(dy,dx),l=Math.sqrt(dx*dx+dy*dy),s=Math.PI/6; return <path d={`M ${sx} ${sy} L ${sx+l*Math.cos(a-s)} ${sy+l*Math.sin(a-s)} A ${l} ${l} 0 0 1 ${sx+l*Math.cos(a+s)} ${sy+l*Math.sin(a+s)} Z`} fill="rgba(255,102,0,0.15)" stroke="#ff6600" strokeWidth="2" opacity={op}/>; }
    if (type === "measure_square") { return <rect x={Math.min(sx,ex)} y={Math.min(sy,ey)} width={Math.abs(ex-sx)} height={Math.abs(ey-sy)} fill="rgba(255,68,255,0.12)" stroke="#ff44ff" strokeWidth="2" opacity={op}/>; }
    return null;
  };

  if (loading) return <div className="h-screen bg-[#121212] flex items-center justify-center"><div className="spinner" /></div>;

  if (!session) return (
    <div className="h-screen bg-[#121212] flex items-center justify-center" data-testid="player-vtt-no-session">
      <div className="text-center"><h2 className="text-xl font-serif text-white mb-2">No Active Session</h2><p className="text-[#888] text-sm">The DM hasn't started a VTT session yet.</p></div>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-[#1a1a1a] overflow-hidden select-none" data-testid="player-vtt-page">
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#222] border-b border-[#333] flex-shrink-0 z-30">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-serif text-white font-medium">{session.name}</h2>
          {member?.character_name && <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-sm">Playing as {member.character_name}</span>}
        </div>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7 text-[#888]" onClick={() => setViewport(v => ({ ...v, scale: Math.min(4, v.scale * 1.25) }))}><ZoomIn className="w-3.5 h-3.5" /></Button>
          <span className="text-[10px] text-[#888] w-10 text-center">{Math.round(viewport.scale * 100)}%</span>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-[#888]" onClick={() => setViewport(v => ({ ...v, scale: Math.max(0.2, v.scale / 1.25) }))}><ZoomOut className="w-3.5 h-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-[#888]" onClick={() => setViewport({ x: 0, y: 0, scale: 1 })}><RotateCcw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Canvas */}
        <div ref={canvasAreaRef} className="flex-1 relative overflow-hidden bg-[#1a1a2e]" style={{ cursor: isPanning ? "grabbing" : "default", touchAction: "none" }}
          onWheel={handleWheel} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={() => { setIsPanning(false); setDrawStart(null); setDrawPreview(null); }}
          onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
          onContextMenu={e => e.preventDefault()}
        >
          <div style={{ transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})`, transformOrigin: "0 0", position: "absolute", top: 0, left: 0 }}>
            {getMapUrl() && <img src={getMapUrl()} alt="map" className="absolute top-0 left-0 pointer-events-none" style={{ imageRendering: "auto", maxWidth: "none" }} draggable={false} data-testid="player-vtt-map-image" onError={(e) => { console.error("Player map load failed:", e.target.src); e.target.style.display = "none"; }} onLoad={(e) => { console.log("Player map loaded:", e.target.naturalWidth, "x", e.target.naturalHeight); }} />}
            {session.grid_enabled && <div className="absolute top-0 left-0 pointer-events-none" style={{ width: 3000, height: 3000, backgroundSize: `${gs}px ${gs}px`, backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)" }} />}
            {/* Fog — masked: vision circles around tokens "punch holes" so the player can see further than just their stand-on tile */}
            {(() => {
              const fogAreas = (session.fog_areas || []).filter(f => !f.revealed);
              const visTokens = (session.tokens || []).filter(t => (t.vision_radius || 0) > 0);
              if (fogAreas.length === 0) return null;
              return (
                <svg className="absolute top-0 left-0 pointer-events-none" style={{ width: 4000, height: 4000, overflow: "visible", zIndex: 50 }}>
                  <defs>
                    <mask id="player-fog-mask">
                      <rect x="0" y="0" width="4000" height="4000" fill="black" />
                      {fogAreas.map(f => (
                        f.cx !== undefined
                          ? <circle key={f.id} cx={f.cx} cy={f.cy} r={f.r} fill="white" />
                          : <rect key={f.id} x={f.x} y={f.y} width={f.width} height={f.height} fill="white" />
                      ))}
                      {/* Each token with vision punches a soft hole in the fog */}
                      {visTokens.map(t => {
                        const tsz = (t.size || 1) * gs;
                        const cx = t.x + tsz / 2;
                        const cy = t.y + tsz / 2;
                        const rPx = (t.vision_radius / 5) * gs;
                        const rId = `vis-fade-${t.id}`;
                        return (
                          <g key={t.id}>
                            <defs>
                              <radialGradient id={rId} cx="50%" cy="50%" r="50%">
                                <stop offset="70%" stopColor="black" stopOpacity="1" />
                                <stop offset="100%" stopColor="black" stopOpacity="0" />
                              </radialGradient>
                            </defs>
                            <circle cx={cx} cy={cy} r={rPx} fill={`url(#${rId})`} />
                          </g>
                        );
                      })}
                    </mask>
                  </defs>
                  <rect x="0" y="0" width="4000" height="4000" fill="rgba(0,0,0,0.92)" mask="url(#player-fog-mask)" />
                </svg>
              );
            })()}
            {/* Map Assets (player-visible only — hide GM layer) */}
            {(session.assets || []).filter(a => a.layer !== "gm").map(a => {
              const def = findAsset(a.asset_key);
              if (!def) return null;
              const sz = a.size || 40;
              return (
                <div key={a.id} className="absolute pointer-events-none" style={{ left: a.x - sz / 2, top: a.y - sz / 2, width: sz, height: sz, transform: `rotate(${a.rotation || 0}deg)`, zIndex: a.layer === "map" ? 5 : 8 }}>
                  <img src={def.url} alt={def.name} draggable={false} className="w-full h-full" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.6))" }} />
                </div>
              );
            })}

            {/* Tokens */}
            {(session.tokens || []).filter(t => t.visible_to_players !== false && t.layer !== "gm").map(token => {
              const sz = (token.size || 1) * gs;
              const isSelected = selectedToken?.id === token.id;
              const hpPct = token.max_hp > 0 ? token.hp / token.max_hp : 1;
              const anim = tokenAnim[token.id];
              return (
                <div key={token.id} className={`vtt-token absolute cursor-pointer group ${anim ? `vtt-anim-${anim}` : ""} ${token.is_dead && !anim ? "vtt-token-dead" : ""}`} style={{ left: token.x, top: token.y, width: sz, height: sz }}
                  onPointerDown={(e) => { e.stopPropagation(); setSelectedToken(token); }}
                  onDoubleClick={(e) => { e.stopPropagation(); setTokenInfoOpen(token); }}
                >
                  {isSelected && <div className="absolute -inset-1 rounded-full border-2 border-yellow-400 animate-pulse" />}
                  <div className="w-full h-full rounded-full border-2 border-white/60 flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: token.color || "#c4a55a" }}>
                    {token.token_image ? <img src={token.token_image.startsWith("http") ? token.token_image : `${FILE_URL}/${token.token_image}`} alt="" className="w-full h-full object-cover rounded-full" draggable={false} /> : <><div className="absolute inset-0 bg-black/20 rounded-full" /><span className="relative text-white font-bold drop-shadow-lg" style={{ fontSize: Math.max(8, sz / 4) }}>{token.name?.substring(0, 5) || "?"}</span></>}
                  </div>
                  {token.max_hp > 0 && <div className="absolute bottom-0.5 left-1 right-1 h-1.5 bg-black/60 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${hpPct*100}%`, backgroundColor: hpPct > 0.5 ? "#22c55e" : hpPct > 0.25 ? "#eab308" : "#ef4444" }} /></div>}
                  <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-black/80 px-1.5 py-0.5 rounded text-[8px] text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none">{token.name}</div>
                </div>
              );
            })}
            {/* SVG shapes + pings + spell effects */}
            <svg className="absolute top-0 left-0 pointer-events-none" style={{ width: 4000, height: 4000, overflow: "visible" }}>
              {(session.spells || []).map(se => {
                const svg = getSpellSVG({ spell: se.spell, x: se.x, y: se.y, rotation: se.rotation, gs });
                if (!svg) return null;
                const schoolColor = SPELL_SCHOOLS[se.spell.school]?.color || "#ef4444";
                return (
                  <g key={se.id}>
                    {svg.type === "circle" && <circle cx={svg.cx} cy={svg.cy} r={svg.r} fill={svg.fill} stroke={svg.stroke} strokeWidth="2" strokeOpacity={svg.strokeOpacity} strokeDasharray="6 3" />}
                    {svg.type === "rect" && <rect x={svg.x} y={svg.y} width={svg.width} height={svg.height} fill={svg.fill} stroke={svg.stroke} strokeWidth="2" strokeOpacity={svg.strokeOpacity} strokeDasharray="6 3" />}
                    {svg.type === "path" && <path d={svg.d} fill={svg.fill} stroke={svg.stroke} strokeWidth="2" strokeOpacity={svg.strokeOpacity} strokeDasharray="6 3" />}
                    {svg.type === "polygon" && <polygon points={svg.points} fill={svg.fill} stroke={svg.stroke} strokeWidth="2" strokeOpacity={svg.strokeOpacity} strokeDasharray="6 3" />}
                    <text x={se.x} y={se.y - 8} fill={schoolColor} fontSize="11" fontWeight="bold" textAnchor="middle" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.8)" }}>{se.spell.name}</text>
                  </g>
                );
              })}
              {shapes.map(s => <MeasureShape key={s.id} shape={s} />)}
              {drawPreview && <MeasureShape shape={drawPreview} preview />}
              {pings.map(p => (
                <g key={p.id} style={{ pointerEvents: "none" }}>
                  <circle cx={p.x} cy={p.y} r={gs * 0.4} fill="none" stroke="#fbbf24" strokeWidth="4" opacity="0.95">
                    <animate attributeName="r" from={gs * 0.3} to={gs * 1.6} dur="1.4s" repeatCount="3" />
                    <animate attributeName="opacity" from="0.95" to="0" dur="1.4s" repeatCount="3" />
                  </circle>
                  <circle cx={p.x} cy={p.y} r={gs * 0.22} fill="#fbbf24" opacity="0.9">
                    <animate attributeName="opacity" values="0.9;0.4;0.9" dur="0.6s" repeatCount="6" />
                  </circle>
                  <circle cx={p.x} cy={p.y} r={gs * 0.08} fill="#fff" />
                  {p.name && (
                    <g transform={`translate(${p.x} ${p.y - gs * 0.55})`}>
                      <rect x={-((p.name.length * 5.5) / 2) - 6} y={-9} width={p.name.length * 5.5 + 12} height={16} rx="4" fill="#1f1b16" stroke="#fbbf24" strokeWidth="1.2" opacity="0.95" />
                      <text x={0} y={2} textAnchor="middle" fontSize={11} fontWeight="600" fill="#fbbf24">{p.name}</text>
                    </g>
                  )}
                </g>
              ))}
            </svg>
          </div>

          {/* Player toolbar (no fog tools) */}
          <VTTToolbar activeTool={activeTool} setActiveTool={setActiveTool} activeLayer="tokens" setActiveLayer={() => {}} gridSize={gs} setGridSize={() => {}} measurePersistent={measurePersistent} setMeasurePersistent={setMeasurePersistent} isDM={false} />
        </div>

        {/* Right panel */}
        <div className="w-64 bg-[#1e1e1e] border-l border-[#333] flex flex-col overflow-hidden">
          {/* Character */}
          {character && (
            <div className="p-3 border-b border-[#333] space-y-2 flex-shrink-0">
              <h3 className="text-sm font-medium text-white">{character.name}</h3>
              <p className="text-xs text-[#888]">{character.race} {character.character_class} Lvl {character.level}</p>
              <div className="grid grid-cols-3 gap-1 text-[10px] text-center">
                {character.armor_class && <div><Shield className="w-3 h-3 mx-auto text-blue-400" /><span className="text-white">{character.armor_class}</span></div>}
                {character.hit_points && <div><Heart className="w-3 h-3 mx-auto text-green-400" /><span className="text-white">{character.hit_points}</span></div>}
              </div>
            </div>
          )}
          {/* Equipment */}
          <div className="p-3 border-b border-[#333] flex-shrink-0">
            <p className="text-[10px] text-[#888] uppercase tracking-wider mb-2"><Package className="w-3 h-3 inline mr-0.5" />Equipment ({equipment.length})</p>
            {equipment.length === 0 ? <p className="text-xs text-[#666] italic">No items</p> : equipment.map(item => (
              <div key={item.id} className="p-1.5 rounded-sm bg-[#2a2a2a] border border-[#444] mb-1">
                <p className={`text-[11px] ${item.equipped ? "text-primary font-medium" : "text-white"}`}>{item.equipped && "E "}{item.name}</p>
                <p className="text-[9px] text-[#888] capitalize">{item.rarity} {item.item_type}</p>
              </div>
            ))}
          </div>
          {/* Dice */}
          <div className="p-3 flex-shrink-0">
            <p className="text-[10px] text-[#888] uppercase tracking-wider mb-2"><Dices className="w-3 h-3 inline mr-0.5" />Quick Roll</p>
            <div className="grid grid-cols-4 gap-1">
              {["1d4","1d6","1d8","1d10","1d12","1d20","2d6","1d100"].map(d => <Button key={d} size="sm" variant="outline" className="h-6 rounded-sm text-[9px] border-[#555] text-[#ccc] hover:border-primary" onClick={() => rollDice(d)}>{d}</Button>)}
            </div>
          </div>
          {/* Roll log */}
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {diceRolls.map(r => <div key={r.id} className="p-1 rounded bg-[#2a2a2a] border border-[#444] text-[10px]"><span className="text-primary">{r.formula}</span><span className="text-[#888]"> = [{r.rolls.join(",")}]{r.modifier ? (r.modifier > 0 ? `+${r.modifier}` : r.modifier) : ""}</span><span className="text-white font-bold ml-1">{r.total}</span></div>)}
          </div>
        </div>

        {/* Token info panel (double-click) */}
        {tokenInfoOpen && (
          <div className="w-72 bg-[#1e1e1e] border-l border-[#333] flex flex-col flex-shrink-0 overflow-hidden z-20">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#333]"><h3 className="text-sm font-medium text-white">{tokenInfoOpen.name}</h3><button onClick={() => setTokenInfoOpen(null)} className="text-[#888] hover:text-white"><X className="w-4 h-4" /></button></div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-[#2a2a2a] rounded p-2 border border-[#444]"><Heart className="w-4 h-4 mx-auto text-green-400 mb-0.5" /><div className="text-sm font-bold text-white">{tokenInfoOpen.hp}/{tokenInfoOpen.max_hp}</div><div className="text-[9px] text-[#888]">HP</div></div>
                <div className="bg-[#2a2a2a] rounded p-2 border border-[#444]"><Shield className="w-4 h-4 mx-auto text-blue-400 mb-0.5" /><div className="text-sm font-bold text-white">{tokenInfoOpen.ac}</div><div className="text-[9px] text-[#888]">AC</div></div>
                <div className="bg-[#2a2a2a] rounded p-2 border border-[#444]"><div className="w-4 h-4 mx-auto rounded-full mb-0.5" style={{ backgroundColor: tokenInfoOpen.color || "#c4a55a" }} /><div className="text-sm font-bold text-white">{tokenInfoOpen.size||1}</div><div className="text-[9px] text-[#888]">Size</div></div>
              </div>
              <div className="grid grid-cols-4 gap-1">
                {["1d20","1d12","1d8","1d6"].map(d => <Button key={d} size="sm" variant="outline" className="h-6 rounded-sm text-[9px] border-[#555] text-[#ccc] hover:border-primary" onClick={() => rollDice(d)}><Dices className="w-2.5 h-2.5 mr-0.5" />{d}</Button>)}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* SHARED HANDOUT POPUP */}
      {sharedHandout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSharedHandout(null)}>
          <div className="bg-[#1e1e1e] border border-[#444] rounded-lg shadow-2xl max-w-lg w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="shared-handout-popup">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#444] bg-primary/10">
              <h3 className="text-base font-serif text-white">{sharedHandout.name}</h3>
              <button onClick={() => setSharedHandout(null)} className="text-[#888] hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            {sharedHandout.image_url && (
              <div className="h-48 overflow-hidden"><img src={sharedHandout.image_url} alt="" className="w-full h-full object-cover" /></div>
            )}
            <div className="p-4 max-h-[40vh] overflow-y-auto">
              <p className="text-sm text-[#ccc] whitespace-pre-wrap leading-relaxed">{sharedHandout.content || "No content"}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerVTTPage;
